import api from '../utils/api';
import { cachedRequest, invalidateReference } from '../utils/referenceCache';

const bust = (prefixes, promise) =>
  promise.then((r) => { [].concat(prefixes).forEach((p) => invalidateReference(p)); return r; });

export const financeService = {
  // Account Subtypes
  getAccountSubtypes: (params) => api.get('/finance/account-subtypes', { params }),

  // Accounts
  getAccounts: (params) => api.get('/finance/accounts', { params }),
  getAccount: (id) => api.get(`/finance/accounts/${id}`),
  createAccount: (data) => api.post('/finance/accounts', data),
  updateAccount: (id, data) => api.put(`/finance/accounts/${id}`, data),
  deleteAccount: (id) => api.delete(`/finance/accounts/${id}`),
  getAccountDimensions: (id) => api.get(`/finance/accounts/${id}/dimensions`),
  saveAccountDimensions: (id, data) => api.put(`/finance/accounts/${id}/dimensions`, data),

  // Dimensions
  getDimensionsConfig: () => api.get('/finance/dimensions/config'),
  updateDimensionsConfig: (data) => api.put('/finance/dimensions/config', data),
  getDimensionTypes: () => api.get('/finance/dimensions/types'),
  getDimensionAccountPolicies: () => api.get('/finance/dimensions/account-policies'),
  updateDimensionType: (slot, data) => api.put(`/finance/dimensions/types/${slot}`, data),
  createDimensionValue: (data) => api.post('/finance/dimensions/values', data),
  updateDimensionValue: (id, data) => api.put(`/finance/dimensions/values/${id}`, data),

  // Transaction Types
  getTransactionTypes: () => api.get('/finance/transaction-types'),
  createTransactionType: (data) => api.post('/finance/transaction-types', data),
  updateTransactionType: (id, data) => api.put(`/finance/transaction-types/${id}`, data),
  deleteTransactionType: (id) => api.delete(`/finance/transaction-types/${id}`),

  // Tax Codes
  getTaxCodes: () => cachedRequest('fin:taxcodes', () => api.get('/finance/tax-codes')),
  createTaxCode: (data) => bust('fin:taxcodes', api.post('/finance/tax-codes', data)),
  updateTaxCode: (id, data) => bust('fin:taxcodes', api.put(`/finance/tax-codes/${id}`, data)),
  deleteTaxCode: (id) => bust('fin:taxcodes', api.delete(`/finance/tax-codes/${id}`)),

  // Journals
  getJournals: (params) => api.get('/finance/journals', { params }),
  getJournal: (id) => api.get(`/finance/journals/${id}`),
  getJournalRelatedTransaction: (id) => api.get(`/finance/journals/${id}/related-transaction`),
  createJournal: (data) => api.post('/finance/journals', data),
  updateJournal: (id, data) => api.put(`/finance/journals/${id}`, data),
  postJournal: (id) => api.post(`/finance/journals/${id}/post`),
  unpostJournal: (id) => api.post(`/finance/journals/${id}/unpost`),
  deleteJournal: (id) => api.delete(`/finance/journals/${id}`),
  uploadAttachment: (id, formData) => api.post(`/finance/journals/${id}/attachments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),

  // Reports
  getTrialBalance: (params) => api.get('/finance/reports/trial-balance', { params }),
  getAccountTransactions: (params) => api.get('/finance/reports/account-transactions', { params }),
  getCashAtHandReport: (params) => api.get('/finance/reports/cash-at-hand', { params }),
  getBalanceSheet: (params) => api.get('/finance/reports/balance-sheet', { params }),
  getIncomeStatement: (params) => api.get('/finance/reports/income-statement', { params }),
  getCashPosition: (params) => api.get('/finance/reports/cash-position', { params }),
  getTaxReport: (params) => api.get('/finance/reports/tax-report', { params }),
  getTaxReportDetail: (params) => api.get('/finance/reports/tax-report-detail', { params }),
  getKRAMonthlyLiability: (params) => api.get('/finance/reports/kra-monthly-liability', { params }),
  getKRAMonthlyLiabilityDetail: (params) => api.get('/finance/reports/kra-monthly-liability-detail', { params }),
  getGLReconciliation: (params) => api.get('/finance/reports/gl-reconciliation', { params }),
  getControlReconciliation: (params) => api.get('/finance/reports/control-reconciliation', { params }),
  autoFixControlReconciliation: (data) => api.post('/finance/reports/control-reconciliation/auto-fix', data),
  getGLIntegrity: (params) => api.get('/finance/reports/gl-integrity', { params }),
  getTrialBalanceByType: (params) => api.get('/finance/reports/trial-balance-by-type', { params }),
  investigateBalance: (data) => api.post('/finance/reports/investigate-balance', data),
  // Item 3: Comparative statements
  getComparativeIncomeStatement: (params) => api.get('/finance/reports/comparative-income-statement', { params }),
  getComparativeBalanceSheet: (params) => api.get('/finance/reports/comparative-balance-sheet', { params }),
  // Item 5: Cash Flow Statement
  getCashFlowStatement: (params) => api.get('/finance/reports/cash-flow', { params }),

  // FX Revaluation
  getFxRevaluationPreview: (params) => api.get('/finance/reports/fx-revaluation/preview', { params }),
  processFxRevaluation: (data) => api.post('/finance/reports/fx-revaluation/process', data),

    // Recurring Journal Templates
    getRecurringTemplates: (params) => api.get('/finance/recurring-templates', { params }),
    getRecurringTemplate: (id) => api.get(`/finance/recurring-templates/${id}`),
    createRecurringTemplate: (data) => api.post('/finance/recurring-templates', data),
    updateRecurringTemplate: (id, data) => api.put(`/finance/recurring-templates/${id}`, data),
    deleteRecurringTemplate: (id) => api.delete(`/finance/recurring-templates/${id}`),
    generateRecurringJournal: (id) => api.post(`/finance/recurring-templates/${id}/generate`),
    getUpcomingRecurringJournals: () => api.get('/finance/recurring-templates/upcoming/list'),

    // Accruals & Deferrals
    getAccrualDeferrals: (params) => api.get('/finance/accruals', { params }),
    getAccrualDeferral: (id) => api.get(`/finance/accruals/${id}`),
    createAccrualDeferral: (data) => api.post('/finance/accruals', data),
    recognizePeriodAccruals: (periodId) => api.post(`/finance/accruals/${periodId}/recognize`),

    // Month-End Close
    getMonthEndCloseStatus: (periodId) => api.get(`/finance/month-end-close/status/${periodId}`),
    runMonthEndAccruals: (periodId) => api.post(`/finance/month-end-close/run-accruals/${periodId}`),
    runMonthEndFxRevaluation: (periodId) => api.post(`/finance/month-end-close/run-fx-revaluation/${periodId}`),
    lockPeriod: (periodId) => api.post(`/finance/month-end-close/lock-period/${periodId}`),
};
